"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { PARTICIPANT_OTP_LENGTH } from "@/lib/participant-otp-constants";

const OTP_LENGTH = PARTICIPANT_OTP_LENGTH;

export interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

export function OTPInput({
  value,
  onChange,
  error = false,
  disabled = false,
  className,
}: OTPInputProps) {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.padEnd(OTP_LENGTH, " ").split("").slice(0, OTP_LENGTH);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d*$/.test(digit)) return;
    const next = value.split("");
    next[index] = digit.slice(-1);
    const joined = next.join("").slice(0, OTP_LENGTH);
    onChange(joined);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const next = value.split("");
      next[index - 1] = "";
      onChange(next.join("").slice(0, OTP_LENGTH));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(pasted);
    const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      className={cn("flex w-full justify-center gap-2", className)}
      onPaste={handlePaste}
    >
      {Array.from({ length: OTP_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            inputRefs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] === " " ? "" : digits[i]}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          className={cn(
            "h-14 w-12 rounded-md border bg-[var(--iris-surface2)] text-center text-xl font-bold text-white outline-none transition-colors focus:ring-2 focus:ring-[var(--iris-red)]",
            error
              ? "border-[var(--iris-red)]"
              : "border-[var(--iris-border)] focus:border-[var(--iris-red)]"
          )}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
